import { Box, HStack, Text } from '@chakra-ui/react';
import React from 'react';
import { MaterialIcon } from '../../ui/MaterialIcon';

interface PrivacyPickerCardProps {
  active: boolean;
  icon: 'groups' | 'lock';
  title: string;
  description: string;
  onClick: () => void;
  disabled: boolean;
}

/**
 * One of the two big privacy-mode picker cards at the top of the
 * Share dialog ("Anyone in this workspace" / "Only specific people").
 *
 * Designed as the visual centerpiece — switching modes is a weighty
 * choice and the cards are sized + bordered to convey that.  The
 * subtle 120ms `transition` keeps the active-state change tactile
 * without feeling toy-like.
 */
export function PrivacyPickerCard({
  active,
  icon,
  title,
  description,
  onClick,
  disabled,
}: PrivacyPickerCardProps): React.ReactElement {
  return (
    <Box
      as="button"
      type="button"
      flex={1}
      onClick={onClick}
      disabled={disabled}
      textAlign="left"
      p={4}
      borderRadius="lg"
      borderWidth="selectable"
      borderColor={active ? 'border.accent' : 'border.subtle'}
      bg={active ? 'accent.bg' : 'bg.surface'}
      transition="all 120ms ease-out"
      _hover={{ borderColor: active ? 'border.accent' : 'border.muted' }}
      _focusVisible={{
        outline: 'var(--chakra-borderWidths-interactive) solid',
        outlineColor: 'border.accent',
        outlineOffset: '2px',
      }}
      _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
      aria-pressed={active}
    >
      <HStack spacing={2} mb={1}>
        <MaterialIcon name={icon} boxSize="18px" color={active ? 'text.accent' : 'text.subtle'} />
        <Text fontWeight="semibold" fontSize="sm" color="text.primary">
          {title}
        </Text>
      </HStack>
      {/* `text.onMutedBg`, not `text.subtle`: the active card now actually
          paints `accent.bg`, and the plain grey is 4.42:1 on that tint (see
          contrast.test.ts's known-ceiling list). Unconditional because #5F5F5F
          vs #6E6E6E is imperceptible on the inactive card's white surface, and
          it still clears AA there (6.39:1). */}
      <Text fontSize="xs" color="text.onMutedBg" lineHeight="short">
        {description}
      </Text>
    </Box>
  );
}
